import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { EditIcon, Delete02Icon, PlusIcon } from "../../../../../assets/icons";
import CompanyServiceModal from "../../../../../components/modals/company/CompanyServiceModal";
import DeletePopup from "../../../../../components/delete-popup";
import {
  createCompanyService,
  updateCompanyService,
  deleteCompanyService,
} from "../../../../../redux/slice/companySlice";
import { usePermission } from "../../../../../hooks/usePermission";
import { useToast } from "../../../../../context/TostContext";

const MODULE = "company_management";

const Services = () => {
  const {id} = useParams();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();
  const toast=useToast();
  const [showModal, setShowModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [expandedServices, setExpandedServices] = useState({});

  const { companyDetail, serviceActionLoading } = useSelector((state) => state.company);

  const toggleExpand = (id) => {
    setExpandedServices((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleUpsertService = (data) => {
    const action = currentItem
      ? updateCompanyService({ companyId: id, id: currentItem.id, data })
      : createCompanyService({ companyId: id, data });

    dispatch(action)
      .unwrap()
      .then(() => {
        toast.success(`Service ${currentItem ? "updated" : "added"} successfully`);
        setShowModal(false);
        setCurrentItem(null);
      })
      .catch((error) => {
        toast.error(error?.message || `Failed to ${currentItem ? "update" : "add"} service`);
      });
  };

  const handleDeleteService = () => {
    dispatch(deleteCompanyService({ companyId: id, id: itemToDelete }))
      .unwrap()
      .then(() => {
        toast.success("Service deleted successfully");
        setShowDeletePopup(false);
        setItemToDelete(null);
      })
      .catch((error) => {
        toast.error(error?.message || "Failed to delete service");
      });
  };

  return (
    <div className="position-relative h-100">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h5 className="fw-bold mb-0" style={{ fontSize: "16px", color: "#444050" }}>
          Services
        </h5>
        {hasPermission(MODULE, "update") && (
          <button
            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
            onClick={() => {
              setCurrentItem(null);
              setShowModal(true);
            }}
          >
            <PlusIcon size={14} />
            Add Service
          </button>
        )}
      </div>

      {companyDetail?.services?.length > 0 ? (
        <div className="row g-3">
          {companyDetail.services.map((service, index) => (
            <div key={index} className="col-12 col-md-6">
              <div className="p-3 border rounded-3 bg-light-subtle position-relative custom-card h-100">
                <div className="position-absolute top-0 end-0 p-2 d-flex gap-2">
                  {hasPermission(MODULE, "update") && (
                    <button
                      className="post-action-btn edit-btn border rounded p-1 bg-white"
                      onClick={() => {
                        setCurrentItem(service);
                        setShowModal(true);
                      }}
                    >
                      <EditIcon width={12} height={12} color="#007bff" />
                    </button>
                  )}
                  {hasPermission(MODULE, "delete") && (
                    <button
                      className="post-action-btn delete-btn border rounded p-1 bg-white"
                      onClick={() => {
                        setItemToDelete(service.id);
                        setShowDeletePopup(true);
                      }}
                    >
                      <Delete02Icon width={12} height={12} color="#FF0000" />
                    </button>
                  )}
                </div>
                <div className="d-flex flex-column h-100 justify-content-between" style={{ paddingRight: "75px" }}>
                  <div>
                    <h6 className="fw-semibold text-dark mb-2 text-break" style={{ fontSize: "14px" }}>
                      {service.title || service.name || "-"}
                    </h6>
                    {service.description && (
                      <p className="text-secondary text-break mb-0 small" style={{ lineHeight: "1.5" }}>
                        {service.description.length > 150 && !expandedServices[service.id] ? (
                          <>
                            {service.description.slice(0, 150)}...{" "}
                            <span
                              className="text-primary fw-semibold ms-1"
                              onClick={() => toggleExpand(service.id)}
                              style={{ cursor: "pointer" }}
                            >
                              Show more
                            </span>
                          </>
                        ) : service.description.length > 150 ? (
                          <>
                            {service.description}{" "}
                            <span
                              className="text-primary fw-semibold ms-1"
                              onClick={() => toggleExpand(service.id)}
                              style={{ cursor: "pointer" }}
                            >
                              Show less
                            </span>
                          </>
                        ) : (
                          service.description
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted text-center py-4 small">No services listed.</p>
      )}

      <CompanyServiceModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setCurrentItem(null);
        }}
        onSubmit={handleUpsertService}
        service={currentItem}
        isLoading={serviceActionLoading}
        title={currentItem ? "Edit Company Service" : "Add Company Service"}
        size="lg"
      />

      <DeletePopup
        show={showDeletePopup}
        onHide={() => {
          setShowDeletePopup(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteService}
        content="Are you sure you want to delete this service? This action cannot be undone."
        saveTitle="Delete"
        disabled={serviceActionLoading}
      />
    </div>
  );
};

export default Services;
